import React from 'react';
import { makeStyles } from '@material-ui/styles';
import {
  InputAdornment,
  Paper,
  TextField,
  Typography,
} from '@material-ui/core';
import { InfoOutlined } from '@material-ui/icons';
import CustomTooltip from '../../../components/CustomTooltip';
import PropTypes from 'prop-types';

const useStyles = makeStyles((theme) => ({
  modalSectionContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(2),
    margin: theme.spacing(2, 0),
    width: '94%',
  },
  flexColumn: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  webkitPaintFix: {
    // Safari/WebKit: caret non visibile
    '& .MuiInputBase-root': {
      transform: 'translateZ(0)', // crea un nuovo layer GPU
      backfaceVisibility: 'hidden', // disattiva il rendering del retro in 3D
    },
  },
  valueWrapper: {
    display: 'flex',
    gap: 10,
    margin: 10,
  },
  paperContainer: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    margin: 20,
    width: '100%',
  },
  paper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-start',
    gap: 10,
    flex: 1,
    padding: 30,
  },
  tooltipWrapper: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
  },
  stdWrapper: {
    display: 'flex',
    justifyContent: 'center',
    gap: 10,
    margin: 10,
  },
}));

const ParametriSection = ({
  mode,
  state,
  handleInputChange,
  handleCustomLimitChange,
  controlloConformita,
}) => {
  const classes = useStyles();

  const renderTPMSection = () => (
    <div className={classes.modalSectionContainer}>
      <div className={`${classes.flexColumn} ${classes.webkitPaintFix}`}>
        <div className={classes.valueWrapper}>
          <Typography>Valore minimo di conformità</Typography>
          <TextField
            name="valoreTPMMinimo"
            value={state.valoreTPMMinimo ?? ''}
            onChange={handleInputChange}
            size="small"
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
          />
          <Typography>%</Typography>
        </div>
        <div className={classes.valueWrapper}>
          <Typography>Valore massimo di conformità</Typography>
          <TextField
            name="valoreTPMMassimo"
            value={state.valoreTPMMassimo ?? ''}
            onChange={handleInputChange}
            size="small"
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
          />
          <Typography>%</Typography>
        </div>
        <div className={classes.stdWrapper}>
          <Typography>Valore Standard</Typography>
          <TextField
            name="valoreTPMConsigliato"
            value={state.valoreTPMConsigliato ?? ''}
            onChange={handleInputChange}
            type="number"
            size="small"
            inputProps={{ min: 0, max: 100, step: 0.1 }}
          />
          <Typography>%</Typography>
        </div>
      </div>
    </div>
  );

  const renderTemperaturaSection = () => (
    <div className={classes.modalSectionContainer}>
      <div className={`${classes.flexColumn} ${classes.webkitPaintFix}`}>
        <div className={classes.valueWrapper}>
          <Typography>Temperatura minima di conformità</Typography>
          <TextField
            name="temperaturaMinima"
            value={state.temperaturaMinima ?? ''}
            onChange={handleInputChange}
            size="small"
            type="number"
          />
          <Typography>°C</Typography>
        </div>
        <div className={classes.valueWrapper}>
          <Typography>Temperatura massima di conformità</Typography>
          <TextField
            name="temperaturaMassima"
            value={state.temperaturaMassima ?? ''}
            onChange={handleInputChange}
            size="small"
            type="number"
          />
          <Typography>°C</Typography>
        </div>
        <div className={classes.stdWrapper}>
          <Typography>Temperatura standard</Typography>
          <TextField
            name="temperaturaConsigliata"
            value={state.temperaturaConsigliata ?? ''}
            onChange={handleInputChange}
            type="number"
            size="small"
          />
          <Typography>°C</Typography>
        </div>
      </div>
    </div>
  );

  const renderOlioFriggitriceSection = () => (
    <div className={classes.modalSectionContainer}>
      <div className={classes.paperContainer}>
        <Paper elevation={2} className={classes.paper}>
          <Typography style={{ fontWeight: 'bold', marginBottom: 10 }}>
            TPM
          </Typography>
          <div className={classes.tooltipWrapper}>
            <Typography>
              Inserire i valori del range di tollerabilità:
            </Typography>
            <CustomTooltip
              title={
                <>
                  <Typography variant="caption">
                    Per valori compresi nel range di tollerabilità all'operatore
                    verrà chiesto di effettuare il controllo pomeridiano.
                  </Typography>
                  <br />
                  <Typography variant="caption">
                    <b>N.B.</b> La non conformità si attiva solo al superamento
                    del <u>limite massimo</u> indicato.
                  </Typography>
                </>
              }
              arrow
              placement="right"
            >
              <InfoOutlined fontSize="small" color="secondary" />
            </CustomTooltip>
          </div>
          <TextField
            fullWidth
            label="Limite minimo"
            name="valoreTPMMinimo"
            type="number"
            value={state.valoreTPMMinimo ?? ''}
            onChange={handleInputChange}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
          <TextField
            fullWidth
            label="Limite massimo"
            name="valoreTPMMassimo"
            type="number"
            value={state.valoreTPMMassimo ?? ''}
            onChange={handleInputChange}
            inputProps={{ min: 0, max: 100, step: 0.1 }}
            InputProps={{
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
            }}
          />
        </Paper>
        <Paper elevation={2} className={classes.paper}>
          <Typography style={{ fontWeight: 'bold', marginBottom: 10 }}>
            Temperatura
          </Typography>
          <TextField
            fullWidth
            label="Temperatura minima"
            name="temperaturaMinima"
            value={state.temperaturaMinima ?? ''}
            onChange={handleInputChange}
            size="small"
            type="number"
            InputProps={{
              endAdornment: <InputAdornment position="end">°C</InputAdornment>,
            }}
          />
          <TextField
            fullWidth
            label="Temperatura massima"
            name="temperaturaMassima"
            value={state.temperaturaMassima ?? ''}
            onChange={handleInputChange}
            size="small"
            type="number"
            InputProps={{
              endAdornment: <InputAdornment position="end">°C</InputAdornment>,
            }}
          />
          <TextField
            fullWidth
            label="Temperatura standard"
            name="temperaturaConsigliata"
            value={state.temperaturaConsigliata ?? ''}
            onChange={handleInputChange}
            type="number"
            size="small"
            InputProps={{
              endAdornment: <InputAdornment position="end">°C</InputAdornment>,
            }}
          />
        </Paper>
      </div>
    </div>
  );

  const renderCustomSection = () => {
    const {
      valoreDecimaleMinimo,
      valoreDecimaleMassimo,
      valoreDecimaleConsigliato,
      valoreInteroMinimo,
      valoreInteroMassimo,
      valoreInteroConsigliato,
    } = state;

    const isDecimal = mode === 'DECIMALE';
    const stepValue = isDecimal ? 0.1 : 1;

    return (
      <div className={classes.modalSectionContainer}>
        <div style={{ width: '100%', maxWidth: '960px' }}>
          <Paper
            elevation={2}
            className={`${classes.paper} ${classes.webkitPaintFix}`}
          >
            <Typography style={{ fontWeight: 'bold', marginBottom: 10 }}>
              {controlloConformita.categoriaPersonalizzataNome ||
                'Categoria Personalizzata'}
            </Typography>
            <div className={classes.flexColumn}>
              <div className={classes.valueWrapper}>
                <Typography>Valore minimo</Typography>
                <TextField
                  autoComplete="off"
                  type="number"
                  name={
                    isDecimal ? 'valoreDecimaleMinimo' : 'valoreInteroMinimo'
                  }
                  value={
                    (isDecimal ? valoreDecimaleMinimo : valoreInteroMinimo) ??
                    ''
                  }
                  onChange={handleCustomLimitChange}
                  size="small"
                  inputProps={{ step: stepValue }}
                />
                <Typography>
                  {controlloConformita.categoriaPersonalizzataUnitaMisura ??
                    'N/A'}
                </Typography>
              </div>
              <div className={classes.valueWrapper}>
                <Typography>Valore massimo</Typography>
                <TextField
                  autoComplete="off"
                  type="number"
                  name={
                    isDecimal ? 'valoreDecimaleMassimo' : 'valoreInteroMassimo'
                  }
                  value={
                    (isDecimal ? valoreDecimaleMassimo : valoreInteroMassimo) ??
                    ''
                  }
                  onChange={handleCustomLimitChange}
                  size="small"
                  inputProps={{ step: stepValue }}
                />
                <Typography>
                  {controlloConformita.categoriaPersonalizzataUnitaMisura ??
                    'N/A'}
                </Typography>
              </div>
              <div className={classes.stdWrapper}>
                <Typography>Valore standard</Typography>
                <TextField
                  autoComplete="off"
                  type="number"
                  name={
                    isDecimal
                      ? 'valoreDecimaleConsigliato'
                      : 'valoreInteroConsigliato'
                  }
                  value={
                    (isDecimal
                      ? valoreDecimaleConsigliato
                      : valoreInteroConsigliato) ?? ''
                  }
                  onChange={handleCustomLimitChange}
                  size="small"
                  inputProps={{ step: stepValue }}
                />
                <Typography>
                  {controlloConformita.categoriaPersonalizzataUnitaMisura ??
                    'N/A'}
                </Typography>
              </div>
            </div>
          </Paper>
        </div>
      </div>
    );
  };

  switch (mode) {
    case 'TPM':
      return renderTPMSection();
    case 'CONTROLLO_TEMPERATURE':
      return renderTemperaturaSection();
    case 'OLIO_FRIGGITRICE':
      return renderOlioFriggitriceSection();
    case 'DECIMALE':
    case 'INTERO': {
      return renderCustomSection();
    }
    default:
      return null;
  }
};

ParametriSection.propTypes = {
  mode: PropTypes.string.isRequired,
  state: PropTypes.object.isRequired,
  handleInputChange: PropTypes.func.isRequired,
  handleCustomLimitChange: PropTypes.func.isRequired,
};

export default ParametriSection;
